/**
 * True when a fetch rejected because the request was aborted — by our own
 * AbortController, by Turbo superseding a visit, or by the browser tearing the
 * page down. Never a defect, never worth reporting.
 *
 * Also the guard for UI rollback: an abort means the user changed their mind or
 * moved on, so the UI must be left ALONE. Only a real failure justifies undoing
 * what the user just did.
 *
 * @param {unknown} error
 * @returns {boolean}
 */
export function isAbortError(error) {
  return error?.name === "AbortError"
}

/**
 * Swallows an abort, re-raises everything else.
 *
 * Deliberately NO variant that logs-and-drops: the Better Stack browser tag
 * exposes no manual capture API, so the only way a real failure reaches
 * monitoring is the global `unhandledrejection` handler the Sentry browser SDK
 * installs. Re-throwing is what keeps that path alive.
 *
 * CONTRACT: `.catch(rethrowUnlessAbort)` may only ever be the LAST link of a
 * promise chain. Put it mid-chain and the following `.then` runs with
 * `undefined` after a swallowed abort.
 *
 * @param {unknown} error
 * @returns {void}
 * @throws {unknown} the original error, unless it was an abort
 */
export function rethrowUnlessAbort(error) {
  if (isAbortError(error)) return

  throw error
}
